import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Lock, LockOpen } from 'lucide-react';
import { useBeforeUnload } from 'react-router-dom';
import toast from 'react-hot-toast';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import { ApiClientError } from '@/services/apiClient';
import { TVChannelOrderingItem, tvChannelService } from '@/services/tvChannelService';

type ChannelCard = TVChannelOrderingItem & {
  locked: boolean;
};

const MAX_RENDERED_SLOTS = 250;
const DEFAULT_RANGE_START = 1;

const buildOrderingSignature = (items: ChannelCard[]) =>
  items.map((item) => `${item.id}:${item.channelNumber}:${item.locked ? 1 : 0}`).join('|');

const reorderVisibleSlotsRespectingLocks = (
  items: ChannelCard[],
  startNumber: number,
  endNumber: number,
  sourceNumber: number,
  targetNumber: number
) => {
  const channelsByNumber = items.reduce<Record<number, ChannelCard>>((acc, item) => {
    acc[item.channelNumber] = item;
    return acc;
  }, {});

  const slots: Array<{ slotNumber: number; channel: ChannelCard | null; locked: boolean }> = [];
  for (let slotNumber = startNumber; slotNumber <= endNumber; slotNumber += 1) {
    const channel = channelsByNumber[slotNumber] ?? null;
    slots.push({
      slotNumber,
      channel,
      locked: channel?.locked ?? false,
    });
  }

  const activeIndex = slots.findIndex((slot) => slot.slotNumber === sourceNumber);
  const overIndex = slots.findIndex((slot) => slot.slotNumber === targetNumber);
  if (activeIndex < 0 || overIndex < 0) {
    return items;
  }

  if (slots[activeIndex].locked) {
    return items;
  }

  const movableSlots = slots.filter((slot) => !slot.locked);
  const fromMovableIndex = movableSlots.findIndex((slot) => slot.slotNumber === sourceNumber);
  if (fromMovableIndex < 0) {
    return items;
  }

  let targetIndex = overIndex;
  if (slots[overIndex].locked) {
    const direction = activeIndex < overIndex ? 1 : -1;
    let cursor = overIndex + direction;

    while (cursor >= 0 && cursor < slots.length) {
      if (!slots[cursor].locked) {
        targetIndex = cursor;
        break;
      }
      cursor += direction;
    }

    if (slots[targetIndex].locked) {
      cursor = overIndex - direction;
      while (cursor >= 0 && cursor < slots.length) {
        if (!slots[cursor].locked) {
          targetIndex = cursor;
          break;
        }
        cursor -= direction;
      }
    }

    if (slots[targetIndex].locked) {
      return items;
    }
  }

  const targetMovableIndex = movableSlots.findIndex((slot) => slot.slotNumber === slots[targetIndex].slotNumber);
  if (targetMovableIndex < 0 || targetMovableIndex === fromMovableIndex) {
    return items;
  }

  const reorderedMovableSlots = arrayMove(movableSlots, fromMovableIndex, targetMovableIndex);
  let movableCursor = 0;
  const rebuiltSlots = slots.map((slot) => {
    if (slot.locked) {
      return slot;
    }

    const nextSlot = reorderedMovableSlots[movableCursor];
    movableCursor += 1;
    return {
      ...slot,
      channel: nextSlot.channel,
      locked: nextSlot.channel?.locked ?? false,
    };
  });

  const nextNumberById = rebuiltSlots.reduce<Record<string, number>>((acc, slot) => {
    if (slot.channel) {
      acc[slot.channel.id] = slot.slotNumber;
    }
    return acc;
  }, {});

  return items.map((item) => ({
    ...item,
    channelNumber: nextNumberById[item.id] ?? item.channelNumber,
  }));
};

interface SortableChannelItemProps {
  sortableId: string;
  item: ChannelCard;
  disabled: boolean;
  movement: number;
  onToggleLock: (id: string) => void;
  isOverlay?: boolean;
}

const SortableChannelItem = memo(({ sortableId, item, disabled, movement, onToggleLock, isOverlay = false }: SortableChannelItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: disabled || item.locked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    opacity: isDragging && !isOverlay ? 0.15 : 1,
  };

  const isMoved = movement !== 0;
  const isInactive = item.active === false;
  const movementLabel = movement > 0 ? `↑${movement}` : `↓${Math.abs(movement)}`;
  const countryFlag = item.country?.flag;
  const countryName = item.country?.name ?? item.country?.nameEnglish ?? 'Country';

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 text-xs">
      <div className="w-7 shrink-0 text-right text-[11px] font-semibold text-gray-700">{item.channelNumber}</div>
      <div
        className={`flex min-w-0 flex-1 items-center rounded border px-2 py-1.5 ${
          item.locked
            ? 'border-amber-300 bg-amber-50'
            : isInactive
              ? 'border-gray-300 bg-gray-100'
            : isMoved
              ? 'border-indigo-200 bg-indigo-50/60'
            : 'border-gray-300 bg-white'
        } ${isDragging ? 'shadow-md' : ''}`}
      >
        <button
          type="button"
          className="mr-2 text-gray-400 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
          disabled={disabled || item.locked}
          aria-label={`Reordenar ${item.title}`}
          title={item.locked ? 'Canal bloqueado' : 'Arrastrar para reordenar'}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <span className={`truncate text-[13px] ${isInactive ? 'text-gray-500' : 'text-blue-700'}`}>
          {item.title}
        </span>
        {isInactive && (
          <span
            className="ml-2 inline-flex shrink-0 items-center rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600"
            title="Canal inactivo"
          >
            Inactivo
          </span>
        )}
        {isMoved && (
          <span
            className="ml-2 inline-flex shrink-0 items-center rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700"
            title="Cambio de posicion"
          >
            {movementLabel}
          </span>
        )}
        {countryFlag && (
          /^https?:\/\//.test(countryFlag) ? (
            <img
              src={countryFlag}
              alt={`${countryName} flag`}
              className="ml-auto h-4 w-6 shrink-0 rounded-sm object-cover"
            />
          ) : (
            <span className="ml-auto shrink-0 text-base leading-none" aria-hidden="true" title={countryName}>
              {countryFlag}
            </span>
          )
        )}
        <button
          type="button"
          onClick={() => onToggleLock(item.id)}
          className={`${countryFlag ? 'ml-1' : 'ml-auto'} pl-2 text-gray-500 hover:text-gray-700`}
          aria-label={item.locked ? `Desbloquear ${item.title}` : `Bloquear ${item.title}`}
          title={item.locked ? 'Desbloquear canal' : 'Bloquear canal'}
        >
          {item.locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
});

interface EmptySlotItemProps {
  sortableId: string;
  slotNumber: number;
  disabled: boolean;
  isOverlay?: boolean;
}

const EmptySlotItem = memo(({ sortableId, slotNumber, disabled, isOverlay = false }: EmptySlotItemProps) => {
  const { setNodeRef: setDropZoneRef, isOver } = useDroppable({
    id: `empty-drop-${slotNumber}`,
    disabled,
  });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    opacity: isDragging && !isOverlay ? 0.15 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 text-xs">
      <div className="w-7 shrink-0 text-right text-[11px] font-semibold text-gray-700">{slotNumber}</div>
      <div
        ref={setDropZoneRef}
        className={`flex min-h-[30px] min-w-0 flex-1 items-center rounded border border-dashed px-2 py-1.5 text-[11px] ${
          isOver || isDragging
            ? 'border-indigo-400 bg-indigo-50 text-indigo-600'
            : 'border-gray-300 bg-gray-50 text-gray-400'
        } ${isDragging ? 'shadow-md' : ''}`}
      >
        <button
          type="button"
          className="mr-2 text-gray-400 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
          disabled={disabled}
          aria-label={`Mover espacio vacio ${slotNumber}`}
          title="Arrastrar espacio vacio"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        Vacio
      </div>
    </div>
  );
});

const TVChannelsOrganize = () => {
  const getColumnCount = (width: number) => {
    if (width >= 1536) return 4;
    if (width >= 1280) return 3;
    if (width >= 640) return 2;
    return 1;
  };

  const unsavedMessage = 'Hay cambios sin guardar. Si sales ahora, se perderán.';
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [channels, setChannels] = useState<ChannelCard[]>([]);
  const [initialNumberById, setInitialNumberById] = useState<Record<string, number>>({});
  const [initialSignature, setInitialSignature] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [columnCount, setColumnCount] = useState(() => getColumnCount(window.innerWidth));
  const [activeDragSlotId, setActiveDragSlotId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const collisionDetectionStrategy = useCallback((args: Parameters<typeof closestCenter>[0]) => {
    const pointerIntersections = pointerWithin(args);
    if (pointerIntersections.length > 0) {
      return pointerIntersections;
    }

    return closestCenter(args);
  }, []);

  const hasChanges = useMemo(
    () => channels.length > 0 && buildOrderingSignature(channels) !== initialSignature,
    [channels, initialSignature]
  );

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasChanges) {
          return;
        }
        event.preventDefault();
        event.returnValue = '';
      },
      [hasChanges]
    )
  );

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!hasChanges) {
        return;
      }

      if (event.defaultPrevented || event.button !== 0) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target as Element | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }

      if (anchor.target && anchor.target !== '_self') {
        return;
      }

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      const isSameRoute =
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash;

      if (nextUrl.origin !== currentUrl.origin || isSameRoute) {
        return;
      }

      const confirmed = window.confirm(unsavedMessage);
      if (!confirmed) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [hasChanges, unsavedMessage]);

  useEffect(() => {
    const handleResize = () => {
      setColumnCount(getColumnCount(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchChannels = useCallback(async () => {
    setValidationError(null);
    setLoadError(null);

    setIsLoading(true);

    try {
      const response = await tvChannelService.getTVChannelsOrdering();

      const nextChannels = response
        .map((item) => ({ ...item, locked: item.locked ?? false }))
        .sort((a, b) => a.channelNumber - b.channelNumber);

      setInitialNumberById(
        nextChannels.reduce<Record<string, number>>((acc, item) => {
          acc[item.id] = item.channelNumber;
          return acc;
        }, {})
      );
      setChannels(nextChannels);
      setInitialSignature(buildOrderingSignature(nextChannels));
    } catch (error) {
      console.error('Error loading channels ordering:', error);
      setLoadError(error instanceof Error ? error.message : 'No se pudo cargar el orden de canales.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchChannels();
  }, [fetchChannels]);

  const handleGetChannels = async () => {
    const fromNumber = Number.parseInt(fromValue, 10);
    const toNumber = Number.parseInt(toValue, 10);

    setValidationError(null);

    if ((fromValue || toValue) && (!Number.isFinite(fromNumber) || !Number.isFinite(toNumber))) {
      setValidationError('Ingresa un rango valido antes de cargar canales.');
      return;
    }

    if (Number.isFinite(fromNumber) && Number.isFinite(toNumber) && fromNumber > toNumber) {
      setValidationError('El rango es invalido: "Desde" no puede ser mayor que "Hasta".');
      return;
    }

    if (Number.isFinite(fromNumber) && Number.isFinite(toNumber)) {
      const requestedSlots = toNumber - fromNumber + 1;
      if (requestedSlots > MAX_RENDERED_SLOTS) {
        setValidationError(`El rango maximo permitido es de ${MAX_RENDERED_SLOTS} posiciones visibles por vez.`);
        return;
      }
    }

    if (hasChanges) {
      const confirmed = window.confirm('Hay cambios sin guardar. ¿Deseas descartarlos y recargar?');
      if (!confirmed) {
        return;
      }
    }
    await fetchChannels();
  };

  const handleToggleLock = (id: string) => {
    setChannels((prev) => prev.map((item) => (item.id === id ? { ...item, locked: !item.locked } : item)));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragSlotId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveDragSlotId(null);
      return;
    }

    const activeSlotId = String(active.id);
    const overSlotId = String(over.id);
    const sourceNumber = Number.parseInt(activeSlotId.replace('slot-', ''), 10);
    const targetNumber = overSlotId.startsWith('empty-drop-')
      ? Number.parseInt(overSlotId.replace('empty-drop-', ''), 10)
      : Number.parseInt(overSlotId.replace('slot-', ''), 10);

    if (!Number.isFinite(sourceNumber) || !Number.isFinite(targetNumber) || sourceNumber === targetNumber) {
      setActiveDragSlotId(null);
      return;
    }

    setChannels((prev) => {
      const sourceChannel = prev.find((item) => item.channelNumber === sourceNumber);
      const targetChannel = prev.find((item) => item.channelNumber === targetNumber);

      if (sourceChannel && !targetChannel) {
        if (sourceChannel.locked) {
          return prev;
        }

        return prev.map((item) =>
          item.id === sourceChannel.id
            ? { ...item, channelNumber: targetNumber }
            : item
        );
      }

      return reorderVisibleSlotsRespectingLocks(
        prev,
        rangeBounds.start,
        rangeBounds.end,
        sourceNumber,
        targetNumber
      );
    });
    setActiveDragSlotId(null);
  };

  const handleDragCancel = () => {
    setActiveDragSlotId(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setValidationError(null);

    try {
      await tvChannelService.updateTVChannelsOrdering({
        channels: channels.map((item) => ({ id: item.id, number: item.channelNumber, locked: item.locked })),
      });

      toast.success('Orden de canales guardado correctamente');
      await fetchChannels();
    } catch (error) {
      console.error('Error saving channels ordering:', error);

      if (error instanceof ApiClientError && error.status === 400) {
        const message = error.data?.message || error.message || 'Datos invalidos para guardar el orden.';
        setValidationError(message);
        toast.error(message);
      } else {
        toast.error(error instanceof Error ? error.message : 'No se pudo guardar el orden de canales.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const channelNumberBounds = useMemo(() => {
    if (channels.length === 0) {
      return { min: NaN, max: NaN };
    }

    let min = channels[0].channelNumber;
    let max = channels[0].channelNumber;
    for (const item of channels) {
      if (item.channelNumber < min) {
        min = item.channelNumber;
      }
      if (item.channelNumber > max) {
        max = item.channelNumber;
      }
    }

    return { min, max };
  }, [channels]);

  useEffect(() => {
    if (!Number.isFinite(channelNumberBounds.max)) {
      return;
    }

    const defaultEnd = Math.min(DEFAULT_RANGE_START + MAX_RENDERED_SLOTS - 1, channelNumberBounds.max);

    setFromValue((current) => (current ? current : String(DEFAULT_RANGE_START)));
    setToValue((current) => (current ? current : String(defaultEnd)));
  }, [channelNumberBounds.max]);

  const rangeBounds = useMemo(() => {
    const fromNumber = Number.parseInt(fromValue, 10);
    const toNumber = Number.parseInt(toValue, 10);

    if (channels.length === 0) {
      if (Number.isFinite(fromNumber) && Number.isFinite(toNumber)) {
        return { start: fromNumber, end: toNumber };
      }
      return { start: NaN, end: NaN };
    }

    if (Number.isFinite(fromNumber) && Number.isFinite(toNumber)) {
      return { start: fromNumber, end: toNumber };
    }

    return {
      start: DEFAULT_RANGE_START,
      end: Math.min(DEFAULT_RANGE_START + MAX_RENDERED_SLOTS - 1, channelNumberBounds.max),
    };
  }, [channelNumberBounds.max, channels.length, fromValue, toValue]);

  const rangeLabel = useMemo(() => {
    if (!Number.isFinite(rangeBounds.start) || !Number.isFinite(rangeBounds.end)) {
      return 'Sin canales';
    }
    return `${rangeBounds.start} - ${rangeBounds.end}`;
  }, [rangeBounds.end, rangeBounds.start]);

  const channelsByNumber = useMemo(
    () =>
      channels.reduce<Record<number, ChannelCard>>((acc, item) => {
        acc[item.channelNumber] = item;
        return acc;
      }, {}),
    [channels]
  );

  const slots = useMemo(() => {
    if (!Number.isFinite(rangeBounds.start) || !Number.isFinite(rangeBounds.end) || rangeBounds.start > rangeBounds.end) {
      return [];
    }

    const result: Array<{ slotNumber: number; channel: ChannelCard | null }> = [];
    for (let slotNumber = rangeBounds.start; slotNumber <= rangeBounds.end; slotNumber += 1) {
      result.push({ slotNumber, channel: channelsByNumber[slotNumber] ?? null });
    }
    return result;
  }, [channelsByNumber, rangeBounds.end, rangeBounds.start]);

  const sortableIds = useMemo(() => slots.map((slot) => `slot-${slot.slotNumber}`), [slots]);

  const channelColumns = useMemo(() => {
    if (slots.length === 0) {
      return [];
    }

    const activeColumnCount = Math.max(1, Math.min(columnCount, slots.length));
    const itemsPerColumn = Math.ceil(slots.length / activeColumnCount);

    return Array.from({ length: activeColumnCount }, (_, index) =>
      slots.slice(index * itemsPerColumn, (index + 1) * itemsPerColumn)
    );
  }, [slots, columnCount]);

  const movementById = useMemo(
    () =>
      channels.reduce<Record<string, number>>((acc, item) => {
        const initialNumber = initialNumberById[item.id];
        acc[item.id] = typeof initialNumber === 'number' ? initialNumber - item.channelNumber : 0;
        return acc;
      }, {}),
    [channels, initialNumberById]
  );

  const activeDragSlot = useMemo(() => {
    if (!activeDragSlotId) {
      return null;
    }

    const slotNumber = Number.parseInt(activeDragSlotId.replace('slot-', ''), 10);
    if (!Number.isFinite(slotNumber)) {
      return null;
    }

    return slots.find((slot) => slot.slotNumber === slotNumber) ?? null;
  }, [activeDragSlotId, slots]);

  return (
    <TVChannelsLayout>
      <div className="space-y-4 pb-28">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Canales</span>
            <div className="flex w-full max-w-[360px]">
              <input
                type="number"
                min={1}
                max={Number.isFinite(channelNumberBounds.max) ? channelNumberBounds.max : undefined}
                value={fromValue}
                onChange={(event) => setFromValue(event.target.value)}
                placeholder="Desde"
                className="w-full rounded-l-md border border-gray-300 py-1.5 pl-3 pr-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <input
                type="number"
                min={1}
                max={Number.isFinite(channelNumberBounds.max) ? channelNumberBounds.max : undefined}
                value={toValue}
                onChange={(event) => setToValue(event.target.value)}
                placeholder="Hasta"
                className="-ml-px w-full rounded-r-md border border-gray-300 py-1.5 pl-3 pr-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <Button
              type="button"
              size="sm"
              loading={isLoading}
              disabled={isSaving}
              onClick={handleGetChannels}
            >
              Buscar canales
            </Button>
            <span className="text-xs font-semibold text-gray-500">Rango: {rangeLabel}</span>
            <span className="text-xs text-gray-500">
              Carga un maximo de {MAX_RENDERED_SLOTS} posiciones por vez para evitar bloqueos del navegador.
            </span>
            {Number.isFinite(channelNumberBounds.max) && (
              <span className="text-xs text-gray-500">
                Canal maximo disponible: {channelNumberBounds.max}
              </span>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          {loadError && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {validationError && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {validationError}
            </div>
          )}

          {slots.length === 0 && !isLoading ? (
            <div className="py-8 text-center text-sm text-gray-500">
              Selecciona un rango y carga hasta {MAX_RENDERED_SLOTS} posiciones para organizar los canales.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetectionStrategy}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                <div
                  className="grid gap-6"
                  style={{ gridTemplateColumns: `repeat(${channelColumns.length || 1}, minmax(0, 1fr))` }}
                >
                  {channelColumns.map((column, columnIndex) => (
                    <div key={`column-${columnIndex}`} className="space-y-2">
                      {column.map((slot) => (
                        slot.channel ? (
                          <SortableChannelItem
                            key={slot.channel.id}
                            sortableId={`slot-${slot.slotNumber}`}
                            item={slot.channel}
                            disabled={isLoading || isSaving}
                            movement={movementById[slot.channel.id] ?? 0}
                            onToggleLock={handleToggleLock}
                          />
                        ) : (
                          <EmptySlotItem
                            key={`empty-${slot.slotNumber}`}
                            sortableId={`slot-${slot.slotNumber}`}
                            slotNumber={slot.slotNumber}
                            disabled={isLoading || isSaving}
                          />
                        )
                      ))}
                    </div>
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeDragSlot?.channel ? (
                  <div className="w-[240px]">
                    <SortableChannelItem
                      sortableId={`slot-${activeDragSlot.slotNumber}`}
                      item={activeDragSlot.channel}
                      disabled
                      movement={movementById[activeDragSlot.channel.id] ?? 0}
                      onToggleLock={() => {}}
                      isOverlay
                    />
                  </div>
                ) : activeDragSlot ? (
                  <div className="w-[240px]">
                    <EmptySlotItem
                      sortableId={`slot-${activeDragSlot.slotNumber}`}
                      slotNumber={activeDragSlot.slotNumber}
                      disabled
                      isOverlay
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-end px-4 py-3 sm:px-6 lg:px-8">
          <Button
            type="button"
            onClick={handleSave}
            loading={isSaving}
            disabled={isLoading || isSaving || !hasChanges || channels.length === 0}
          >
            Guardar cambios
          </Button>
        </div>
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelsOrganize;
